import React, { useCallback, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl-next';
import { EmptyState, Link } from '@atlassian/search-dialog';
import { Image } from '../../../common/no-results';
import { messages } from '../../../messages';
import { messages as CrossProductMessages } from './messages';
import { type ScreenSpecificProps } from '../../product-router/product/result-provider/result-renderer';
import { getTrigger, isNewTab, onAdvancedSearchSelected } from '../../../common/analytics';
import { AdvancedSearchLinkSubjectId, useAnalytics } from '../../../common/analytics';
import { useQuery } from '../../query-context';

export const NoResults = ({
	urlGeneratorForNoResultsScreen = () => '',
}: Pick<ScreenSpecificProps, 'urlGeneratorForNoResultsScreen'>) => {
	const intl = useIntl();
	const { query } = useQuery();
	const advancedSearchUrl = urlGeneratorForNoResultsScreen(query);
	const { fireAnalyticsEvent } = useAnalytics();

	const onAdvancedSearchClicked = useCallback(
		(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
			fireAnalyticsEvent(
				onAdvancedSearchSelected({
					trigger: getTrigger(e),
					actionSubjectId: AdvancedSearchLinkSubjectId.NO_RESULTS,
					isLoading: false,
					newTab: isNewTab(e),
					isRovoCrossProductSearchLink: true,
				}),
			);
		},
		[fireAnalyticsEvent],
	);

	const content = useMemo(() => {
		return !advancedSearchUrl ? (
			<FormattedMessage {...messages.common_empty_state_body_no_advanced_search} />
		) : (
			<FormattedMessage
				{...CrossProductMessages.cross_product_empty_state_body}
				values={{
					a: (s: React.ReactNode[]) => (
						<Link onClick={onAdvancedSearchClicked} href={advancedSearchUrl} target="_blank">
							{s}
						</Link>
					),
				}}
			/>
		);
	}, [onAdvancedSearchClicked, advancedSearchUrl]);

	return (
		<EmptyState
			title={intl.formatMessage(messages.common_empty_state_heading)}
			Image={Image}
			content={content}
		/>
	);
};

export default NoResults;
